import { createContext, useContext, useMemo } from 'react';
import classnames from 'classnames';

import style from './app-brand.less';
import { OldLogos, RebrandLogos } from './logo/assets';
import { useEnableColorRebrand } from '../feature-flags/hooks/use-enable-color-rebrand';
import { useEnableShipStationRebrandLogos } from '../feature-flags/hooks/use-enable-shipstation-rebrand-logos';

import type React from 'react';
import './concepts/shipstation-concept-layer-legacy.less';
import './concepts/shipstation-concept-layer-default.less';

/**
 * The name of our application.
 */
export const ShipStation = 'ShipStation';

export const Themes = {
  Default: 'default-theme' as const,
  Legacy: 'legacy-theme' as const,
};

export type BrandDetails = {
  AppHeaderLogoSrc: string;
  SetupPageLogoSrc: string;
  theme: string;
};

const BrandContext = createContext<BrandDetails>({} as BrandDetails);

/**
 * Returns various re-/brand specific values
 */
export const useAppBrandContext = (): BrandDetails => useContext(BrandContext);

/**
 * Hook that is internally aware of the various Rebranding feature flag(s) state
 * and serves the necessary assets/flags to context-aware Components
 */
const useGetBrandDetails = (): BrandDetails => {
  const enableRebrandTheme = useEnableColorRebrand();
  const enableRebrandLogos = useEnableShipStationRebrandLogos();
  const theme = enableRebrandTheme ? Themes.Default : Themes.Legacy;

  return useMemo(
    () => ({
      AppHeaderLogoSrc: enableRebrandLogos ? RebrandLogos.AppHeaderLogo : OldLogos.AppHeaderLogo,
      SetupPageLogoSrc: enableRebrandLogos ? RebrandLogos.SetupPageLogo : OldLogos.SetupPageLogo,
      theme,
    }),
    [enableRebrandLogos, theme],
  );
};

/**
 * Context Provider that controls applicability of the the 2024 Rebranding efforts
 * to presentation-level components throughout the app.
 */
export const AppBrandContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const brandDetails = useGetBrandDetails();

  return (
    <BrandContext.Provider value={brandDetails}>
      <div className={classnames(style['app-brand'], brandDetails.theme)}>{children}</div>
    </BrandContext.Provider>
  );
};
